import { useEffect, useState } from "react";
import { ApiRequestError, getIntegrationStatus } from "../lib/client";
import type { SalesforceIntegrationStatus } from "../lib/types";

type State =
  | { kind: "loading" }
  | { kind: "ready"; sf: SalesforceIntegrationStatus }
  | { kind: "error"; message: string };

/**
 * Shows the subscriber's Salesforce connection status and instance URL,
 * sourced from `GET /api/v1/config/<subscriber_id>/`.
 */
export function SalesforceStatus({
  subscriberId,
  refreshKey = 0,
}: {
  subscriberId: string;
  /** Bump to force a re-fetch (e.g. after saving credentials). */
  refreshKey?: number;
}) {
  const [state, setState] = useState<State>({ kind: "loading" });

  // biome-ignore lint/correctness/useExhaustiveDependencies: refreshKey is a deliberate trigger dep, not a computation input
  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    getIntegrationStatus(subscriberId)
      .then((res) => {
        if (!cancelled)
          setState({
            kind: "ready",
            sf: res.subscriber_integrations.salesforce,
          });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          kind: "error",
          message:
            err instanceof ApiRequestError
              ? err.message
              : "Failed to load status.",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [subscriberId, refreshKey]);

  if (state.kind === "loading") {
    return (
      <div className="sf-status">
        <span className="status-dot pending" /> Loading Salesforce status…
      </div>
    );
  }
  if (state.kind === "error") {
    return (
      <div className="sf-status">
        <span className="status-dot error" /> Salesforce: {state.message}
      </div>
    );
  }

  const { status, instance_url } = state.sf;
  return (
    <div className="sf-status">
      <span className={`status-dot ${status}`} />
      <span className="status-label">Salesforce: {status}</span>
      {instance_url ? (
        <a
          className="instance-url"
          href={instance_url}
          target="_blank"
          rel="noreferrer"
          title="Open Salesforce instance in a new tab"
        >
          {instance_url}
        </a>
      ) : (
        <span className="muted">no instance URL</span>
      )}
    </div>
  );
}
